import { Box, Button, Divider, LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

export default function SurveyStepper({
  t,
  activeStep,
  setActiveStep,
  stepsCount = 3,
  loading = false,
  isBceValid = true,
  isSectorValid,
  onFirstStepNext,
  advanceAfterFirstStep = false,
  onFinish,
  hideBackOnFirstStep = false,
  children,
}) {
  const theme = useTheme();

  const progressValue = Math.floor((activeStep / stepsCount) * 100);

  const handleBack = () => {
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const handleNext = async () => {
    if (activeStep === 0 && onFirstStepNext) {
      await onFirstStepNext();
      if (advanceAfterFirstStep) {
        setActiveStep((s) => Math.min(s + 1, stepsCount - 1));
      }
      return;
    }

    if (activeStep >= stepsCount - 1) {
      onFinish?.();
      return;
    }

    setActiveStep((s) => Math.min(s + 1, stepsCount - 1));
  };

  const nextDisabled =
    loading || (activeStep === 0 && !isBceValid) || (activeStep === 1 && !isSectorValid);

  return (
    <>
      {/* Barre de progression */}
      <Box sx={{ width: '100%', px: 0, py: 1 }}>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{
            height: 6,
            borderRadius: 8,
            mb: '15px',
            bgcolor: '#e8e8e8',
            '& .MuiLinearProgress-bar': {
              bgcolor: '#0175f5',
              borderRadius: 8,
            },
          }}
        />
      </Box>

      {/* Contenu */}
      {children}

      {/* Séparateur + Boutons */}
      <Divider
        sx={{
          mt: activeStep === 2 ? '40px' : '148px',
          borderColor: '#0d93d2',
        }}
      />
      <Box
        sx={{
          display: 'flex',
          mb: 10,
          justifyContent:
            hideBackOnFirstStep && activeStep === 0 ? 'flex-end' : 'space-between',
          mt: 2,
        }}
      >
        {!(hideBackOnFirstStep && activeStep === 0) && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleBack}
            startIcon={
              theme.direction === 'rtl' ? (
                <KeyboardArrowRight sx={{ position: 'relative', top: -1 }} />
              ) : (
                <KeyboardArrowLeft sx={{ position: 'relative', top: -1 }} />
              )
            }
            sx={{
              borderRadius: 9999,
              borderWidth: 2,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontWeight: 700,
              fontSize: '0.85rem',
              px: 2.5,
              py: 1.5,
              minWidth: 0,
              color: '#0d93d2',
              borderColor: '#58d7ff',
              '& .MuiButton-startIcon': { mr: 3 },
              '&:hover': {
                borderWidth: 2,
                borderColor: '#58d7ff',
                backgroundColor: 'rgba(88,215,255,0.08)',
              },
            }}
            disabled={loading}
          >
            {t('survey.create.stepper.previous')}
          </Button>
        )}

        <Button
          variant="outlined"
          size="small"
          onClick={handleNext}
          endIcon={
            theme.direction === 'rtl' ? (
              <KeyboardArrowLeft sx={{ position: 'relative', top: -1 }} />
            ) : (
              <KeyboardArrowRight sx={{ position: 'relative', top: -1 }} />
            )
          }
          disabled={nextDisabled}
          sx={{
            borderRadius: 9999,
            borderWidth: 2,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontWeight: 700,
            fontSize: '0.85rem',
            px: 2.5,
            py: 1.5,
            minWidth: 0,
            color: '#0d93d2',
            borderColor: '#58d7ff',
            '& .MuiButton-endIcon': { ml: 3 },
            '&:hover': {
              borderWidth: 2,
              borderColor: '#58d7ff',
              backgroundColor: 'rgba(88,215,255,0.08)',
            },
          }}
        >
          {t('survey.create.stepper.next')}
        </Button>
      </Box>
    </>
  );
}